import { mostSpecificBrandPathOf } from '@atlas/core-utils/classification'
import type { AxisSelection } from '@atlas/types'
import { AxisCode, MappingSystem } from '@atlas/types'
import type { QueryClient } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { notification } from '@/notifications/notification-store'
import { type ClassifyTuple, classificationApi } from '@/services/classification.service'

/** React-query hooks for the classification admin screens. */

/**
 * Roots of the two file-list key families a classification commit invalidates.
 * Exported so the realtime query bridge and `useDiscoverFiles` derive from the
 * same constants rather than repeating the strings.
 */
export const FILES_KEY = 'files'
export const DISCOVER_FILES_KEY = 'discover-files'

export const useAxes = () => useQuery({ queryKey: ['axes'], queryFn: classificationApi.listAxes })

/** Query key for the session bootstrap read — shared so a prefetch and every consumer hit the same cache entry. */
export const CLASSIFICATION_BOOTSTRAP_KEY = ['classification-bootstrap']

/**
 * The whole active axis + value registry in one call (classification-bootstrap).
 * A long, bounded `staleTime` (not `Infinity`) — the taxonomy is admin-rare to
 * change, but a session left open across an edit should eventually self-heal
 * rather than never revalidating.
 */
export const useClassificationBootstrap = (options: { enabled?: boolean } = {}) =>
	useQuery({
		queryKey: CLASSIFICATION_BOOTSTRAP_KEY,
		queryFn: classificationApi.bootstrap,
		staleTime: 30 * 60 * 1000, // 30 minutes
		enabled: options.enabled ?? true,
	})

export const useValues = (axisId: string | null) =>
	useQuery({
		queryKey: ['values', axisId],
		queryFn: () => classificationApi.listValues(axisId as string),
		enabled: !!axisId,
	})

/** Values of an axis identified by its stable CODE (e.g. 'geography'). */
export const useValuesByAxisCode = (axisCode: string) => {
	const { data: axes = [] } = useAxes()
	const axis = axes.find((a) => a.code === axisCode)
	return useValues(axis?.axisId ?? null)
}

export const useMappings = (valueId: string | null) =>
	useQuery({
		queryKey: ['mappings', valueId],
		queryFn: () => classificationApi.listMappings(valueId as string),
		enabled: !!valueId,
	})

export const useCreateAxis = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: classificationApi.createAxis,
		onSuccess: () => qc.invalidateQueries({ queryKey: ['axes'] }),
	})
}

export const useCreateValue = (axisId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: { code: string; label: string; parentId?: string }) =>
			classificationApi.createValue(axisId, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['values', axisId] }),
	})
}

export const useRetireValue = (axisId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (valueId: string) => classificationApi.retireValue(axisId, valueId),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['values', axisId] }),
	})
}

export const useUpdateValue = (axisId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ valueId, body }: { valueId: string; body: { label?: string; status?: string } }) =>
			classificationApi.updateValue(axisId, valueId, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['values', axisId] }),
	})
}

export const useReparentValue = (axisId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ valueId, parentId }: { valueId: string; parentId: string | null }) =>
			classificationApi.reparentValue(axisId, valueId, parentId),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['values', axisId] }),
	})
}

/**
 * One axis value with the axis it belongs to, resolved from the bootstrap registry.
 *
 * There is no `GET /values/{valueId}` — a value is only addressable through its axis — so a page
 * that holds nothing but a value id (the mapping and availability editors, both reached by id)
 * would otherwise have no way to name what it is editing. The bootstrap read already carries the
 * whole registry and is cached for 30 minutes, so this costs nothing those pages were not already
 * paying for.
 *
 * Returns `null` while the registry loads or when the id matches nothing — a caller renders the
 * id it was given rather than an empty heading.
 */
export const useValueWithAxis = (valueId: string | null) => {
	const { data: bootstrap } = useClassificationBootstrap()
	return useMemo(() => {
		// Both halves are checked independently: a bootstrap read can land carrying no
		// `valuesByAxis` at all, and `Object.entries(undefined)` throws rather than yielding
		// nothing — which took down the whole page instead of falling back to the id.
		if (!valueId || !bootstrap?.valuesByAxis) return null
		// `valuesByAxis` is keyed by axis CODE — see the bootstrap's `valuesByAxisDTO[axisCode]`.
		// Matching the key against `axis.axisId` resolves nothing, and the caller silently gets
		// `null` rather than an error, so the axis is found by `code`.
		for (const [axisCode, values] of Object.entries(bootstrap.valuesByAxis)) {
			const value = values?.find((v) => v.valueId === valueId)
			if (value) return { value, axis: bootstrap.axes?.find((a) => a.code === axisCode) ?? null }
		}
		return null
	}, [valueId, bootstrap])
}

/**
 * A value's availability rows — the markets it is valid in.
 *
 * NO rows is not "unavailable": it means nobody has described the value, and the reads treat that
 * as unrestricted. The editor has to show that difference, so the empty answer is data here rather
 * than an absence.
 */
export const useAvailability = (valueId: string | null) =>
	useQuery({
		queryKey: ['availability', valueId],
		queryFn: () => classificationApi.listAvailability(valueId as string),
		enabled: !!valueId,
	})

/**
 * Replace a value's availability set.
 *
 * REPLACE, not add: the endpoint takes the whole set, so the editor sends what the value should be
 * available in and the server reconciles. That is what makes un-checking a market work at all —
 * an additive write could never remove one.
 *
 * Availability changes which brands appear in which market, so the invalidation reaches every
 * classification-derived read rather than just this value's row.
 */
export const useSetAvailability = (valueId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (geoValueIds: string[]) => classificationApi.setAvailability(valueId, geoValueIds),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ['availability', valueId] })
			invalidateClassifiedReads(qc)
		},
	})
}

export const useCreateMapping = (valueId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: { system: string; geoValueId?: string | null; externalCode: string }) =>
			classificationApi.createMapping(valueId, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['mappings', valueId] }),
	})
}

/**
 * Drop every read whose answer is derived from classification.
 *
 * Unbounded on purpose. A tag write is not local to the file it names: discovery, every
 * facet and count, and every page composed through the classification core — Explore, the
 * brand page, the product page and its tuple options — change with it, on surfaces the
 * writer never opened. Naming those keys one by one is what left a freshly classified
 * asset invisible on the product page until its five-minute staleTime lapsed, and the list
 * would have to be re-audited for every new read. The handful of reads classification
 * cannot affect (the axis registry, the signed-in user) cost less to refetch than that.
 *
 * Only ACTIVE queries refetch; the rest are marked stale and re-read when next mounted,
 * which is exactly the case that was broken — classifying on one screen, then navigating.
 *
 * COALESCED rather than narrowed. The breadth above is deliberate and stays; what was
 * wrong is the frequency. Eight call sites fire this, several of them per file, so a
 * five-file upload triggered it dozens of times — and because a work-order page mounts
 * about ten discovery-backed queries, that became hundreds of `/files/discover` calls
 * (228 observed in one ten-minute session) for a result that is identical whether it runs
 * once or forty times. A short trailing window collapses a burst into one invalidation
 * while a single write still refreshes promptly.
 *
 * The window is armed by the first call and NOT reset by later ones, matching the query
 * bridge's own coalescing. A resetting debounce would be wrong here: calls arriving
 * steadily less than a window apart — which is exactly what a multi-file upload produces —
 * would push the deadline forward indefinitely and starve the invalidation entirely, so
 * the reads it exists to refresh would never refresh at all. Arming once bounds the delay
 * at `COALESCE_WINDOW_MS` no matter how long the burst runs.
 */
const COALESCE_WINDOW_MS = 300

let pendingInvalidation: ReturnType<typeof setTimeout> | null = null

export const invalidateClassifiedReads = (qc: QueryClient): void => {
	if (pendingInvalidation) return
	pendingInvalidation = setTimeout(() => {
		pendingInvalidation = null
		void qc.invalidateQueries()
	}, COALESCE_WINDOW_MS)
}

/** Test-only: drop a pending coalesced invalidation so one spec cannot leak into the next. */
export const resetClassifiedReadsCoalescing = (): void => {
	if (pendingInvalidation) clearTimeout(pendingInvalidation)
	pendingInvalidation = null
}

/** Commit a batch of file classifications in one transactional call. */
export const useClassifyBatch = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (tuples: ClassifyTuple[]) => classificationApi.classifyBatch(tuples),
		onSuccess: () => invalidateClassifiedReads(qc),
	})
}

/**
 * Classify an object — add ONE new assertion. The tuple id is allocated
 * server-side rather than computed from the client's own (possibly stale)
 * snapshot, which is what let two concurrent adds land in the same tuple instead
 * of getting one each. Resolves with the allocated tuple id.
 */
export const useClassify = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ objectType, objectId, tuple }: { objectType: string; objectId: string; tuple: AxisSelection }) =>
			classificationApi.classify(objectType, objectId, tuple),
		onSuccess: () => invalidateClassifiedReads(qc),
	})
}

/** Remove ONE of an object's assertions, leaving its siblings intact. */
export const useRemoveTuple = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ objectType, objectId, tupleId }: { objectType: string; objectId: string; tupleId: number }) =>
			classificationApi.removeTuple(objectType, objectId, tupleId),
		onSuccess: () => invalidateClassifiedReads(qc),
	})
}

/**
 * Add or replace the name a value carries in one market — a `taxonomy_title` mapping
 * scoped to a geography value, which is how a per-market name is stored.
 */
export const useSetMarketName = (valueId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ geoValueId, name }: { geoValueId: string | null; name: string }) =>
			classificationApi.createMapping(valueId, {
				system: MappingSystem.TAXONOMY_TITLE,
				geoValueId,
				externalCode: name,
			}),
		onError: (error: Error) => notification.error(`Could not save the name: ${error.message}`),
		onSuccess: () => notification.success('Market name saved.'),
		onSettled: () => invalidateNameReads(qc, valueId),
	})
}

export const useRemoveMarketName = (valueId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ geoValueId }: { geoValueId: string | null }) =>
			classificationApi.deleteMapping(valueId, MappingSystem.TAXONOMY_TITLE, geoValueId),
		onError: (error: Error) => notification.error(`Could not remove the name: ${error.message}`),
		onSuccess: () => notification.success('Market name removed.'),
		onSettled: () => invalidateNameReads(qc, valueId),
	})
}

/**
 * Edit an axis value's `meta` — the taxonomy half of a brand or product's record.
 *
 * Separate from `useUpdateValue`, which is the admin registry's label/status editor: this
 * writes only `meta`, and invalidates the PAGE reads that render it rather than the axis
 * value list, because that is where the change is visible.
 */
export const useUpdateValueMeta = (axisId: string, valueId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (meta: Record<string, unknown>) => classificationApi.updateValue(axisId, valueId, { meta }),
		onError: (error: Error) => notification.error(`Could not save: ${error.message}`),
		onSuccess: () => notification.success('Taxonomy meta saved.'),
		onSettled: () => {
			for (const queryKey of [['values', axisId], ['brand-page'], ['product-page'], CLASSIFICATION_BOOTSTRAP_KEY]) {
				void qc.invalidateQueries({ queryKey })
			}
		},
	})
}

/**
 * One axis's values from the cached bootstrap: in hierarchy order, carrying `depth` and
 * `meta`.
 *
 * Preferred over `useValuesByAxisCode` wherever a picker needs to show the hierarchy or
 * read a value's meta. That hook calls the per-axis endpoint, which answers flat DTOs with
 * neither — so a geography picker built on it rendered 111 countries as an unordered list
 * with no regions, and paid a request for the privilege.
 */
export const useBootstrapValues = (axisCode: string) => {
	const { data: bootstrap } = useClassificationBootstrap()
	return useMemo(() => bootstrap?.valuesByAxis?.[axisCode] ?? [], [bootstrap, axisCode])
}

/**
 * {@link mostSpecificBrandPathOf} over the cached registry, plus whether the registry read has settled.
 *
 * Takes an object's whole `brand` tag list, not its first code: an order from Workfront carries
 * the brand and its product side by side, and the first of them may be the brand alone.
 *
 * `path` is empty BOTH while the registry is loading and once it has settled with no brand
 * tag — a caller that cannot tell the two apart treats "not resolved yet" as "no brand" and
 * renders a false-negative empty state (or, worse, narrows a brand-wide read by nothing and
 * briefly lists a sibling product's content). `isLoading` is what a caller checks first.
 *
 * A persistent registry failure also settles to an empty path rather than falling back to
 * the raw code: a single-element path is indistinguishable from a real one-value brand, and
 * would resurrect the very product/brand conflation this hook exists to resolve.
 */
export const useBrandPath = (
	codes: string | readonly string[] | null | undefined,
): { path: string[]; isLoading: boolean } => {
	const { data: bootstrap, isLoading, isError } = useClassificationBootstrap()
	return useMemo(() => {
		if (!bootstrap) return { path: [], isLoading: isLoading && !isError }
		return { path: mostSpecificBrandPathOf(bootstrap.valuesByAxis?.[AxisCode.BRAND] ?? [], codes), isLoading: false }
	}, [bootstrap, isLoading, isError, codes])
}

/**
 * One object's values on one attribute axis, and the control to set them.
 *
 * Attributes are object-wide (no tuple) and matched by equality, so this is a plain
 * read-and-replace rather than anything tuple-scoped. Setting an empty array clears the
 * attribute, so the object states nothing on that axis.
 */
export const useObjectAttribute = (objectType: string, objectId: string | null, axisCode: string) =>
	useQuery({
		queryKey: ['object-attribute', objectType, objectId, axisCode],
		queryFn: async (): Promise<string[]> => {
			const rows = await classificationApi.getObjectAttributes(objectType, [objectId as string], [axisCode])
			return rows[0]?.attributes?.[axisCode] ?? []
		},
		enabled: !!objectId,
	})

export const useSetObjectAttribute = (objectType: string, objectId: string, axisCode: string) => {
	const qc = useQueryClient()
	const key = ['object-attribute', objectType, objectId, axisCode]
	return useMutation({
		mutationFn: (valueCodes: string[]) => classificationApi.setAttribute(objectType, objectId, axisCode, valueCodes),
		onMutate: async (valueCodes) => {
			await qc.cancelQueries({ queryKey: key })
			const previous = qc.getQueryData<string[]>(key)
			qc.setQueryData<string[]>(key, valueCodes)
			return { previous }
		},
		onError: (error: Error, _codes, context) => {
			if (context?.previous) qc.setQueryData(key, context.previous)
			notification.error(`Could not save: ${error.message}`)
		},
		onSuccess: () => notification.success('Saved.'),
		onSettled: () => void qc.invalidateQueries({ queryKey: key }),
	})
}

/**
 * Every read that renders a market name. A name lives on the axis value, so the pages
 * that show it are invalidated alongside the mapping list itself.
 */
function invalidateNameReads(qc: QueryClient, valueId: string) {
	for (const queryKey of [['mappings', valueId], ['brand-page'], ['product-page']]) {
		void qc.invalidateQueries({ queryKey })
	}
}

/**
 * Retires one value onto another, moving every classification and mapping that pointed at
 * it. Not optimistic: the merge is a server-side cascade whose reach — how many rows
 * collided and were dropped rather than moved — is only known once it has run, so there is
 * no honest cache patch to apply, and every list that could show either value is refetched.
 */
export const useMergeValue = (axisId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ valueId, into }: { valueId: string; into: string }) =>
			classificationApi.mergeValue(axisId, valueId, into),
		onSuccess: (result) => {
			notification.success(
				`Merged. ${result.classifications} classification${result.classifications === 1 ? '' : 's'} and ${result.mappings} mapping${result.mappings === 1 ? '' : 's'} moved.`,
			)
		},
		onError: (error: Error) => notification.error(`Could not merge: ${error.message}`),
		onSettled: () => {
			for (const queryKey of [
				['values', axisId],
				['brands'],
				['products'],
				['explore'],
				CLASSIFICATION_BOOTSTRAP_KEY,
			]) {
				void qc.invalidateQueries({ queryKey })
			}
		},
	})
}

/**
 * Retire an axis — soft, never a delete: `classification` and `object_attribute` reference
 * it by id, so the rows filed against it survive and simply stop being offered.
 */
export const useRetireAxis = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (axisId: string) => classificationApi.retireAxis(axisId),
		onError: (error: Error) => notification.error(`Could not retire the axis: ${error.message}`),
		onSuccess: (axis) => notification.success(`Axis “${axis.name}” retired.`),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: ['axes'] })
			void qc.invalidateQueries({ queryKey: CLASSIFICATION_BOOTSTRAP_KEY })
		},
	})
}

/** Edit an axis's own fields. `code` and `kind` are not among them — see the axis page. */
export const useUpdateAxis = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			axisId,
			body,
		}: {
			axisId: string
			body: Partial<{ name: string; valueType: string; isHierarchical: boolean; status: string }>
		}) => classificationApi.updateAxis(axisId, body),
		onError: (error: Error) => notification.error(`Could not save the axis: ${error.message}`),
		onSuccess: () => notification.success('Axis saved.'),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: ['axes'] })
			void qc.invalidateQueries({ queryKey: CLASSIFICATION_BOOTSTRAP_KEY })
		},
	})
}

/** One axis from the registry, by id. */
export const useAxis = (axisId: string | null) => {
	const { data: axes = [], isLoading } = useAxes()
	return { axis: axes.find((a) => a.axisId === axisId) ?? null, isLoading }
}

/** One value with its axis, by ids — the value-detail page's subject. */
export const useValue = (axisId: string | null, valueId: string | null) => {
	const { data: values = [], isLoading } = useValues(axisId)
	return { value: values.find((v) => v.valueId === valueId) ?? null, isLoading }
}
